<template>
    <select
        class="CFormSelect input cursor-pointer"
        :class="{ 'text-gray-500 dark:text-txt-body': !selectedOption }"
        :value="modelValue || value"
        v-bind="$attrs"
        @input="handleInput"
    >
        <option v-if="placeholder" :disabled="!allowEmpty" :selected="!modelValue" :value="undefined">
            {{ placeholder }}
        </option>

        <option
            v-for="option in normalizedOptions"
            :key="option.value"
            :disabled="option.disabled"
            :selected="option.value === (modelValue || value)"
            :value="option.value"
        >
            <template v-if="useSlots().default">
                <slot :option="option" />
            </template>
            <template v-else>
                {{ option.label }}
            </template>
        </option>
    </select>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { Extractor } from '@/utils/normalizeOption'
import type { optionType } from '#shared/types/dictionary'
import { normalizeOptions } from '@/utils/normalizeOption'

const props = defineProps({
    options: {
        type: [Array, Object] as PropType<Array<any> | Record<string, any> | optionType[]>,
        default: () => []
    },
    labelKey: {
        type: [Function, String, Number] as PropType<Extractor>,
        default: undefined
    },
    valueKey: {
        type: [Function, String, Number] as PropType<Extractor>,
        default: undefined
    },
    disabledKey: {
        type: [Function, String, Number] as PropType<Extractor>,
        default: undefined
    },
    placeholder: {
        type: String,
        default: ''
    },
    value: {
        type: [String, Number, Object],
        default: ''
    },
    modelValue: {
        type: [String, Number, Object],
        default: ''
    },
    allowEmpty: Boolean
})

const emit = defineEmits(['input', 'update:modelValue'])

const selectedOption = ref(props.modelValue || props.value)

const normalizedOptions = computed(() => {
    // If options is already an array of optionType, use it directly
    if (Array.isArray(props.options) && props.options.length > 0 && 
        typeof props.options[0] === 'object' && props.options[0] !== null &&
        'id' in props.options[0] && 'label' in props.options[0] && 'value' in props.options[0]) {
        return props.options.map(option => ({
            value: option.value,
            label: option.label,
            disabled: option.disabled || false
        }))
    }
    
    return normalizeOptions(props.options, {
        value: props.valueKey,
        label: props.labelKey,
        disabled: props.disabledKey
    })
})

const handleInput = (e: Event) => {
    const target = e.target as HTMLSelectElement
    selectedOption.value = target.value
    console.log("CFormSelect - Selected: ", target.value)
    emit('input', target.value)
    emit('update:modelValue', target.value)
    console.log("CFormSelect modelValue is: ", props.modelValue)
}

watch(
    () => props.modelValue || props.value,
    (value) => {
        selectedOption.value = value
    }
)

onMounted(() => {
    selectedOption.value = props.modelValue || props.value
})
</script>
